import { useState, useEffect } from "react"
import { useGlobalContext } from "../data/GlobalContext"
import { apiJSONURL } from "../../App"
import { channels, depositChannel } from "../data/Payment"

// VIP ranking mapping
const vipRank = {
  EMERALD: 1,
  SAPPHIRE: 2,
  RUBY: 3,
  DIAMOND: 4,
  ALEXANDRITE: 5,
}

// Reusable header component
const DepositSelectionTools = ({ onBack, header, subheader }) => (
  <div className="deposit-selection-tools">
    <button onClick={onBack} className="transaction-back-btn">
      <img src="/images/icon/black-left-arrow.webp" alt="Back" />
    </button>
    <label>{header}</label>
    {subheader && (
      <label className="deposit-selection-channel-sublabel">{subheader}</label>
    )}
  </div>
)

// Returns header info based on active section and selected bank/wallet
const getHeaderInfo = (section, selectedBank) => {
  const headers = {
    bank_selection: { header: "Bank Account", subheader: "Select a Bank" },
    ewallet_selection: {
      header: "E-Wallet Account",
      subheader: "Choose Wallet",
    },
    crypto_selection: {
      header: "Crypto Currency",
      subheader: "Choose Currency",
    },
    otc_selection: { header: "Over the Counter", subheader: null },
    open_bank: {
      header: selectedBank ? `Cash in from ${selectedBank.name}` : "",
      subheader: null,
    },
    promotion: { header: "Apply Promotion", subheader: "Select a promotion" },
  }
  return headers[section] || { header: "", subheader: "" }
}

export default function Deposit({ onClose }) {
  const { username, vipDetails } = useGlobalContext()

  // State
  const [activeSection, setActiveSection] = useState(null)
  const [, setSectionHistory] = useState([])
  const [selectedBank, setSelectedBank] = useState(null)
  const [bankInput, setBankInput] = useState("")
  const [depositType, setDepositType] = useState("")
  const [inputError, setInputError] = useState(false)
  const [selectedPromotion, setSelectedPromotion] = useState("")

  // Right below your other React states
  const [direpayForm2Data, setDirepayForm2Data] = useState({
    data: "",
    platform: "",
    lang: "",
    action: "",
  })

  // Additional states for processing and alert messages
  const [processing, setProcessing] = useState(false)
  const [alertMessage, setAlertMessage] = useState("")

  useEffect(() => {
    if (direpayForm2Data.action && depositType === "crypto") {
      setTimeout(() => {
        const form = document.getElementById("direpayForm2")
        if (form) {
          form.submit()
        } else {
          console.error("Form direpayForm2 not found")
        }
      }, 100) // delay 100ms to ensure DOM updates
    }
  }, [direpayForm2Data])

  const showMessageWithTimeout = (message, setFn) => {
    setFn(message)
    setTimeout(() => {
      setFn("")
    }, 6000)
  }

  // Lock scroll when in a sub-section
  useEffect(() => {
    const container = document.querySelector(".wallet-transaction-container")
    if (container) container.style.overflow = activeSection ? "hidden" : "auto"
    return () => container && (container.style.overflow = "auto")
  }, [activeSection])

  // Submission handlers grouped by deposit type
  const submitHandlers = {
    bank: {
      Unionbank: (input) => console.log("Unionbank submit:", input),
      Metrobank: (input) => console.log("Metrobank submit:", input),
      BDO: (input) => console.log("BDO submit:", input),
      BPI: (input) => console.log("BPI submit:", input),
      Maybank: (input) => console.log("Maybank submit:", input),
      "PS Bank": (input) => console.log("PS Bank submit:", input),
      RCBC: (input) => console.log("RCBC submit:", input),
      "Bank of Commerce": (input) =>
        console.log("Bank of Commerce submit:", input),
      Chinabank: (input) => console.log("Chinabank submit:", input),
      "Robinsons Bank": (input) => console.log("Robinsons Bank submit:", input),
    },
    ewallet: {
      GCash: (input) => console.log("GCash submit:", input),
      Maya: (input) => console.log("Maya submit:", input),
      "Fortune Pay": (input) => console.log("Fortune Pay submit:", input),
      QRPH: (input) => console.log("QRPH submit:", input),
    },
    crypto: {
      "TRC20-USDT": (input) => console.log("TRC20-USDT submit:", input),
      "ERC20-USDT": (input) => console.log("ERC20-USDT submit:", input),
      "TRC20-USDC": (input) => console.log("TRC20-USDC submit:", input),
      "ERC20-USDC": (input) => console.log("ERC20-USDC submit:", input),
      BTC: (input) => console.log("Bitcoin submit:", input),
      ETH: (input) => console.log("Ethereum submit:", input),
    },
  }

  // Deposit limits by type
  const depositLimits = {
    crypto: { min: 200, max: Infinity },
    bank: { min: 200, max: 50000 },
    ewallet: { min: 100, max: 300000 },
    otc: { min: 100, max: 30000 },
  }

  // Currency formatting/parsing helpers
  const formatCurrency = (value) => {
    const num = parseInt(value.replace(/[^\d]/g, ""), 10)
    return !isNaN(num) ? `₱ ${num.toLocaleString("en-US")}` : ""
  }
  const parseCurrency = (value) => parseInt(value.replace(/[^\d]/g, ""), 10)

  const handleInputChange = (e) => {
    const formatted = formatCurrency(e.target.value)
    setBankInput(formatted)
    const numericValue = parseCurrency(formatted)
    let min = 100
    let max = 300000

    if (depositType === "ewallet" && selectedBank) {
      if (selectedBank.name === "GCash" || selectedBank.name === "QRPH") {
        max = 30000
      } else if (
        selectedBank.name === "Maya" ||
        selectedBank.name === "Fortune Pay"
      ) {
        max = 50000
      }
    } else if (depositType && depositLimits[depositType]) {
      min = depositLimits[depositType].min
      max = depositLimits[depositType].max
    }

    setInputError(
      !isNaN(numericValue) && (numericValue < min || numericValue > max)
    )
  }

  // Navigation functions
  const handleChangeSection = (newSection) => {
    if (newSection !== "promotion") setBankInput("")
    setInputError(false)

    setSectionHistory((prev) =>
      activeSection ? [...prev, activeSection] : prev
    )
    setActiveSection(newSection)

    // 💡 Re-select "No Bonus" every time a method section is selected

    const recoveryBonus = bonusList.find(
      (bonus) =>
        bonus.bonusnameEN && bonus.bonusnameEN.includes("Recovery Bonus 8%")
    )

    if (recoveryBonus) {
      setSelectedPromotion({
        id: recoveryBonus.bonusid,
        promotion: recoveryBonus.bonusnameEN,
        bonus: recoveryBonus.bonusnameEN,
        label: recoveryBonus.bonusnameEN,
        minDeposit: 250,
      })
    } else {
      const noBonus = bonusList.find((bonus) =>
        bonus.bonusnameEN.includes("Default Non-Bonus Deposit")
      )

      if (noBonus) {
        setSelectedPromotion({
          id: noBonus.bonusid,
          promotion: "No Bonus",
          bonus: "No Bonus",
          label: "No Bonus",
          minDeposit: 0,
        })
      }
    }
  }

  const handleBack = () => {
    setInputError(false)
    if (activeSection !== "promotion") {
      setBankInput("")
      setSelectedPromotion(null)
    }
    setSectionHistory((prev) => {
      if (prev.length) {
        const lastSection = prev[prev.length - 1]
        setActiveSection(lastSection)
        return prev.slice(0, -1)
      }
      setActiveSection(null)
      return []
    })
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    const numericValue = parseCurrency(bankInput)
    const { min, max } = depositLimits[depositType] || {}
    if (isNaN(numericValue) || numericValue < min || numericValue > max) return
    if (!selectedBank) return
    const handler = submitHandlers[depositType]?.[selectedBank.name]
    if (handler) {
      handler(bankInput)
    } else {
      console.log(
        `No ${depositType} submit handler for ${selectedBank.name}`,
        bankInput
      )
    }
  }

  const depositAmount = parseCurrency(bankInput)
  const isSubmitDisabled =
    !bankInput ||
    isNaN(depositAmount) ||
    depositAmount < depositLimits[depositType].min ||
    depositAmount > depositLimits[depositType].max ||
    !selectedPromotion ||
    (selectedPromotion && depositAmount < selectedPromotion.minDeposit)

  // Determine eligible and disabled promotions based on VIP rank
  const getUserRank = () =>
    vipDetails && vipRank[vipDetails.vipName] ? vipRank[vipDetails.vipName] : 0

  const [bonusList, setBonusList] = useState([])

  const handleSubmitBtn = async (e) => {
    e.preventDefault()
    if (processing) return
    setProcessing(true)

    const form = e.target.closest("form")
    const formData = new FormData(form)

    const rawAmtValue = formData.get("amt") || ""
    const numericValue = parseCurrency(rawAmtValue)

    formData.set("amt", numericValue.toString())

    try {
      const response = await fetch(apiJSONURL, {
        method: "POST",
        body: new URLSearchParams(formData),
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      })

      const data = await response.json()

      if (data.s) {
        console.log(data.s)
      } else {
        showMessageWithTimeout(data.emsg, setAlertMessage)
      }
      const redirectURL = data.url || data.action
      if (redirectURL) {
        window.location.href = redirectURL
      }
    } catch (error) {
      showMessageWithTimeout(
        "An error occurred, please try again.",
        setAlertMessage
      )
    } finally {
      setProcessing(false)
    }
  }

  const handleSubmitCryptoBtn = async (e) => {
    e.preventDefault()
    setProcessing(true)

    const form = e.target.closest("form")
    const formData = new FormData(form)

    // format the amount
    const rawAmtValue = formData.get("amt") || ""
    const numericValue = parseCurrency(rawAmtValue)
    formData.set("amt", numericValue.toString())

    try {
      const response = await fetch(apiJSONURL, {
        method: "POST",
        body: new URLSearchParams(formData),
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      })

      const data = await response.json()

      if (data.data != null) {
        // Correctly set your form data clearly here
        setDirepayForm2Data({
          data: data.data,
          platform: data.platform,
          lang: data.lang,
          action: data.action,
        })

        // explicitly submit the form to DirePay after the state updates
        setTimeout(() => {
          const form2 = document.getElementById("direpayForm2")
          if (form2) {
            form2.submit()
          } else {
            console.error("DirePay Form not found")
          }
        }, 100)
      } else {
        showMessageWithTimeout(
          data.emsg || "Transaction error.",
          setAlertMessage
        )
      }
    } catch (error) {
      console.error("Submission error:", error)
      showMessageWithTimeout(
        "An error occurred, please try again.",
        setAlertMessage
      )
    } finally {
      setProcessing(false)
    }
  }

  const handleOTC = async (e) => {
    e.preventDefault()
    setProcessing(true)

    const form = e.target.closest("form")
    const formData = new FormData(form)

    const rawAmtValue = formData.get("amt") || ""
    const numericValue = parseCurrency(rawAmtValue)

    formData.set("amt", numericValue.toString())

    // bonusid is already included via hidden input
    try {
      const response = await fetch(apiJSONURL, {
        method: "POST",
        body: new URLSearchParams(formData),
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      })

      const data = await response.json()

      if (data.s) {
        console.log(data.s)
      } else {
        showMessageWithTimeout(data.emsg, setAlertMessage)
      }
      const redirectURL = data.url || data.action
      if (redirectURL) {
        window.location.href = redirectURL
      }
    } catch (error) {
      showMessageWithTimeout(
        "An error occurred, please try again.",
        setAlertMessage
      )
    } finally {
      setProcessing(false)
    }
  }

  const newMemberBonusRequirements = {
    198: 200,
    199: 300,
    200: 400,
    201: 500,
    202: 1000,
  }

  const ttacMinDeposit = {
    118: 1000,
    119: 2000,
    120: 3000,
    121: 4000,
    165: 6000,
  }

  useEffect(() => {
    const fetchBonusList = async () => {
      try {
        const response = await fetch(apiJSONURL, {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: `cmd=getBonusList&u=${username}`,
        })

        const data = await response.json()
        const bonuses = data.bonus || []
        // console.log("Fetched bonuses:", bonuses)

        const now = new Date()
        const day = now.getDate()
        const hour = now.getHours()

        const filteredBonuses = bonuses.filter((bonus) => {
          if (!bonus || !bonus.bonusid) return false

          const bonusID = parseInt(bonus.bonusid, 10)
          const usernameIsReseller = username.startsWith("ANOBAR")

          if (usernameIsReseller && bonusID !== 75) return false
          if ([54, 61, 64, 71, 76, 80, 81, 87].includes(bonusID)) return false

          if ([77, 78, 79].includes(bonusID)) {
            if ((day === 15 && hour < 12) || (day === 18 && hour > 12))
              return false
          }

          return true
        })
        // console.log("Filtered bonuses:", filteredBonuses)
        // Sort bonuses by VIP eligibility
        // setBonusList(filteredBonuses)
        // const defaultNoBonus = filteredBonuses.find((bonus) =>
        //   bonus.bonusnameEN.includes("Default Non-Bonus Deposit")
        // )

        // if (defaultNoBonus) {
        //   setSelectedPromotion({
        //     id: defaultNoBonus.bonusid,
        //     promotion: "No Bonus",
        //     bonus: "No Bonus",
        //     label: "No Bonus",
        //     minDeposit: 0,
        //   })
        // }
        const recoveryBonus = filteredBonuses.find(
          (bonus) =>
            bonus.bonusnameEN && bonus.bonusnameEN.includes("Recovery Bonus 8%")
        )
        // console.log("Recovery Bonus found:", recoveryBonus)

        if (recoveryBonus) {
          setSelectedPromotion({
            id: recoveryBonus.bonusid,
            promotion: recoveryBonus.bonusnameEN,
            bonus: recoveryBonus.bonusnameEN,
            label: recoveryBonus.bonusnameEN,
            minDeposit: 250,
          })
        } else {
          const defaultNoBonus = filteredBonuses.find(
            (bonus) =>
              bonus.bonusnameEN &&
              bonus.bonusnameEN.includes("Default Non-Bonus Deposit")
          )

          if (defaultNoBonus) {
            setSelectedPromotion({
              id: defaultNoBonus.bonusid,
              promotion: "No Bonus",
              bonus: "No Bonus",
              label: "No Bonus",
              minDeposit: 0,
            })
          }
        }

        setBonusList(filteredBonuses)
      } catch (error) {
        console.error("Error fetching bonuses:", error)
      }
    }

    if (username) fetchBonusList()
  }, [username])

  return (
    <div className="wallet-transaction-container">
      <div className="deposit-container">
        <button
          onClick={() => {
            setBankInput("")
            setInputError(false)
            onClose && onClose()
          }}
          className="transaction-global-close-btn"
        >
          <img src="/images/icon/white-close-btn.webp" alt="Close" />
        </button>
        <div className="deposit-selection-channel">
          <label>Cash In</label>
          <div className="deposit-selection-channel-sublabel">
            Select your prepared method.
          </div>
          {!activeSection && (
            <div className="deposit-channel-list">
              <ul>
                {depositChannel.map((data) => (
                  <li
                    key={data.id}
                    onClick={() => {
                      if (data.onClick === "otc_selection")
                        setDepositType("otc")
                      handleChangeSection(data.onClick)
                    }}
                    // style={
                    //   data.status === "(Offline)"
                    //     ? {
                    //         filter: "grayscale(100%)",
                    //         pointerEvents: "none",
                    //         opacity: "0.5",
                    //       }
                    //     : {}
                    // }
                  >
                    <img src={data.icon} alt={data.name} />
                    {data.name}
                    <img src="/images/icon/black-right-arrow.webp" alt="Next" />
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div
            className={`transaction-label-notice ${inputError ? "error" : ""}`}
          >
            <label>
              <img
                src="/images/icon/notice-icon.webp"
                alt=""
                className="notice-icon"
              />{" "}
              Only use our official deposit channels
            </label>
            <p>
              All deposit transactions must be made through our official
              automated payment system on our site.
              <strong>Beware of Scammers!</strong> We will never ask for
              deposits outside these official channels. If you encounter
              suspicious requests, please report them immediately.
            </p>
          </div>
        </div>

        {activeSection && (
          <div className="deposit-channel-content">
            <DepositSelectionTools
              onBack={handleBack}
              {...getHeaderInfo(activeSection, selectedBank)}
            />
            {activeSection === "bank_selection" && (
              <ul className="deposit-item-selection">
                {channels.bank
                  .filter((bank) => bank.id <= 11)
                  .map((data) => (
                    <li
                      key={data.id}
                      onClick={() => {
                        setSelectedBank(data)
                        setDepositType("bank")
                        handleChangeSection("open_bank")
                      }}
                    >
                      <img src={data.logo} alt={data.name} /> {data.name}
                    </li>
                  ))}
              </ul>
            )}
            {activeSection === "ewallet_selection" && (
              <ul className="deposit-item-selection">
                {channels.ewallet.map((data) => (
                  <li
                    key={data.id}
                    onClick={() => {
                      setSelectedBank(data)
                      setDepositType("ewallet")
                      handleChangeSection("open_bank")
                    }}
                  >
                    <img src={data.logo} alt={data.name} /> {data.name}
                  </li>
                ))}
              </ul>
            )}
            {activeSection === "crypto_selection" && (
              <ul className="deposit-item-selection">
                {channels.crypto.map((data) => (
                  <li
                    key={data.id}
                    onClick={() => {
                      setSelectedBank(data)
                      setDepositType("crypto")
                      handleChangeSection("open_bank")
                    }}
                  >
                    <img src={data.logo} alt={data.name} /> {data.name}
                  </li>
                ))}
              </ul>
            )}
            {activeSection === "otc_selection" && (
              <div className="deposit-promotion-section">
                <form onSubmit={handleOTC} className="transaction-form">
                  <div className="deposit-promo-notice">
                    <strong>Notice:</strong> Please contact us if you have not
                    received your credits after 10 minutes after the payment.
                  </div>

                  <div
                    className={`transaction-field-deposit ${
                      inputError ? "error" : ""
                    }`}
                  >
                    <input name="cmd" type="hidden" value="deposit-request" />
                    <input name="u" type="hidden" value={username} />
                    <input
                      name="returnUrl"
                      type="hidden"
                      value="/profile#activity"
                    />
                    <input name="method" type="hidden" value="otc" />
                    {selectedPromotion && (
                      <input
                        type="hidden"
                        name="bonusid"
                        value={selectedPromotion.id}
                      />
                    )}
                    <label>Amount</label>
                    <input
                      type="text"
                      value={bankInput}
                      onChange={handleInputChange}
                      placeholder="Enter amount"
                      name="amt"
                    />
                  </div>

                  <div
                    className={`transaction-field-message ${
                      inputError ? "error" : ""
                    }`}
                  >
                    From ₱100 up to ₱30,000 per transaction. Contact support to
                    request a higher limit.
                  </div>

                  <div className="transaction-field-message error">
                    {alertMessage}
                  </div>

                  <div className="deposit-promotion-section">
                    <div className="deposit-promotion-content">
                      {bonusList.map((bonus) => {
                        const bonusName = bonus.bonusnameEN.includes(
                          "Default Non-Bonus Deposit"
                        )
                          ? "No Bonus"
                          : bonus.bonusnameEN

                        const minRequired =
                          newMemberBonusRequirements[bonus.bonusid] ||
                          ttacMinDeposit[bonus.bonusid] ||
                          0

                        const depositValue = parseCurrency(bankInput)
                        const isTTAC = Object.prototype.hasOwnProperty.call(
                          ttacMinDeposit,
                          bonus.bonusid
                        )

                        const isEligible = isTTAC
                          ? depositValue === minRequired //  exact match for ttacMinDeposit
                          : depositValue >= minRequired //  normal behavior for others

                        return (
                          <div
                            key={bonus.bonusid}
                            className={`deposit-promotion-item ${
                              selectedPromotion?.id === bonus.bonusid
                                ? "selected"
                                : ""
                            } ${
                              !isEligible && minRequired > 0 ? "disabled" : ""
                            }`}
                            onClick={() => {
                              if (!isEligible && minRequired > 0) {
                                showMessageWithTimeout(
                                  `Minimum deposit for this bonus is ₱${minRequired}.`,
                                  setAlertMessage
                                )
                                return
                              }

                              setSelectedPromotion({
                                id: bonus.bonusid,
                                promotion: bonus.bonusnameEN,
                                bonus: bonus.bonusnameEN,
                                label: bonus.bonusnameEN,
                                minDeposit: minRequired,
                              })
                            }}
                            style={
                              !isEligible && minRequired > 0
                                ? { opacity: 0.5, pointerEvents: "none" }
                                : {}
                            }
                          >
                            <div className="deposit-promotion-details">
                              <img
                                src="/images/icon/deposit-promotion-icon.webp"
                                alt={bonusName}
                              />
                              <div className="deposit-promotion-featured">
                                <label>{bonusName}</label>
                                {minRequired > 0 && (
                                  <>
                                    <small>
                                      {Object.prototype.hasOwnProperty.call(
                                        ttacMinDeposit,
                                        bonus.bonusid
                                      )
                                        ? `Exact Amount ₱${minRequired}`
                                        : `Min ₱${minRequired}`}
                                    </small>

                                    {isTTAC && (
                                      <>
                                        <br />
                                        <small
                                          style={{
                                            display: "block",
                                          }}
                                        >
                                          5X rollover req (slots only)
                                        </small>
                                      </>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                            <div className="deposit-promotion-divider"></div>
                            <div className="deposit-promotion-cta">
                              <div>
                                {selectedPromotion?.id === bonus.bonusid
                                  ? "Applied"
                                  : "Apply"}
                              </div>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                  <br />
                  <div className="deposit-submit-btn-container">
                    <button
                      type="submit"
                      className="deposit-submit-btn"
                      disabled={isSubmitDisabled || processing}
                      onClick={
                        depositType === "crypto"
                          ? handleSubmitCryptoBtn
                          : handleSubmitBtn
                      }
                      style={
                        isSubmitDisabled || processing
                          ? { background: "#9c9c9c", cursor: "not-allowed" }
                          : {}
                      }
                    >
                      {processing ? "Processing..." : "Continue Cash in"}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {activeSection === "promotion" && (
              <div className="deposit-promotion-section">
                <div className="deposit-promotion-content">
                  {bonusList.map((bonus) => {
                    const bonusName = bonus.bonusnameEN.includes(
                      "Default Non-Bonus Deposit"
                    )
                      ? "No Bonus"
                      : bonus.bonusnameEN

                    const minRequired =
                      newMemberBonusRequirements[bonus.bonusid] ||
                      ttacMinDeposit[bonus.bonusid] ||
                      0

                    const depositValue = parseCurrency(bankInput)

                    const isTTAC = Object.prototype.hasOwnProperty.call(
                      ttacMinDeposit,
                      bonus.bonusid
                    )
                    const isEligible = isTTAC
                      ? depositValue === minRequired //  exact match for ttacMinDeposit
                      : depositValue >= minRequired //  normal behavior for others

                    return (
                      <div
                        key={bonus.bonusid}
                        className={`deposit-promotion-item ${
                          selectedPromotion?.id === bonus.bonusid
                            ? "selected"
                            : ""
                        } ${!isEligible && minRequired > 0 ? "disabled" : ""}`}
                        onClick={() => {
                          if (!isEligible && minRequired > 0) {
                            showMessageWithTimeout(
                              `Minimum deposit for this bonus is ₱${minRequired}.`,
                              setAlertMessage
                            )
                            return
                          }

                          setSelectedPromotion({
                            id: bonus.bonusid,
                            promotion: bonus.bonusnameEN,
                            bonus: bonus.bonusnameEN,
                            label: bonus.bonusnameEN,
                            minDeposit: minRequired,
                          })
                        }}
                        style={
                          !isEligible && minRequired > 0
                            ? { opacity: 0.5, pointerEvents: "none" }
                            : {}
                        }
                      >
                        <div className="deposit-promotion-details">
                          <img
                            src="/images/icon/deposit-promotion-icon.webp"
                            alt={bonusName}
                          />
                          <div className="deposit-promotion-featured">
                            <label>{bonusName}</label>
                            {minRequired > 0 && (
                              <>
                                <small>
                                  {Object.prototype.hasOwnProperty.call(
                                    ttacMinDeposit,
                                    bonus.bonusid
                                  )
                                    ? `Exact Amount ₱${minRequired}`
                                    : `Min ₱${minRequired}`}
                                </small>

                                {isTTAC && (
                                  <>
                                    <br />
                                    <small
                                      style={{
                                        display: "block",
                                      }}
                                    >
                                      5X rollover req (slots only)
                                    </small>
                                  </>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                        <div className="deposit-promotion-divider"></div>
                        <div className="deposit-promotion-cta">
                          <div>
                            {selectedPromotion?.id === bonus.bonusid
                              ? "Applied"
                              : "Apply"}
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}

            {activeSection === "open_bank" && selectedBank && (
              <div>
                <form onSubmit={handleSubmit} className="transaction-form">
                  <div className="deposit-promo-notice">
                    {["GCash", "QRPH", "Maya"].includes(selectedBank?.name) && (
                      <>
                        <strong>Notice:</strong> Please contact us if you have
                        not received your credits after 10 minutes after the
                        payment.
                      </>
                    )}
                    {selectedBank?.name === "Fortune Pay" && (
                      <>
                        <p>
                          <strong>Notice:</strong> Before submitting this
                          request, ensure that you have downloaded the Fortune
                          Pay Philippines APP and that you have sufficient funds
                          in your wallet.
                        </p>
                        <br />
                        <p>
                          We recommend that you verify your Fortune Pay Account
                          up to the Legitimate level.
                        </p>
                      </>
                    )}
                    {selectedBank?.name === "GCash" && (
                      <>
                        <br />
                        <br />
                        <p>
                          Please always use the latest QR code and never reuse
                          it.
                        </p>
                      </>
                    )}
                    {selectedBank?.name === "QRPH" && (
                      <>
                        <br />
                        <br />
                        <p>Please do not pay to the same QR code twice. it.</p>
                      </>
                    )}
                    {depositType === "crypto" && (
                      <>
                        <p>
                          <strong>Notice:</strong> Please contact us if you have
                          not received your credits after 10 minutes after the
                          payment.
                        </p>
                        <br />
                        <p>Please do not pay to the same QR code twice.</p>
                      </>
                    )}
                    {depositType === "bank" && (
                      <>
                        <p>
                          <strong>Notice:</strong> Please contact us if you have
                          not received your credits after 10 minutes after the
                          payment.
                        </p>
                      </>
                    )}
                  </div>
                  <div
                    className={`transaction-field-deposit ${
                      inputError ? "error" : ""
                    }`}
                  >
                    <input
                      name="cmd"
                      type="hidden"
                      value={selectedBank?.cmd2 || ""}
                    />

                    {selectedPromotion && (
                      <input
                        type="hidden"
                        name="bonusid"
                        value={selectedPromotion.id}
                      />
                    )}
                    {depositType !== "crypto" && (
                      <>
                        <input
                          name="returnUrl"
                          type="hidden"
                          value="/profile/"
                        />

                        <input
                          name="method"
                          type="hidden"
                          value={selectedBank?.method || ""}
                        />
                        <input
                          name="mbc"
                          type="hidden"
                          value={selectedBank?.mbc || ""}
                        />
                      </>
                    )}
                    {depositType == "crypto" && (
                      <>
                        <input
                          name="token"
                          type="hidden"
                          value={selectedBank?.name || ""}
                        />
                      </>
                    )}

                    <input name="u" type="hidden" value={username} />

                    <label>Amount</label>
                    <input
                      type="text"
                      value={bankInput}
                      onChange={handleInputChange}
                      placeholder="Enter amount"
                      name="amt"
                    />
                  </div>
                  <div
                    className={`transaction-field-message ${
                      inputError ? "error" : ""
                    }`}
                  >
                    <div>
                      {selectedBank?.name === "GCash" ||
                      selectedBank?.name === "QRPH"
                        ? "From ₱100 up to ₱30,000 per transaction. Contact support to request a higher limit."
                        : selectedBank?.name === "Maya"
                        ? "From ₱100 up to ₱50,000 per transaction. Contact support to request a higher limit."
                        : selectedBank?.name === "Fortune Pay"
                        ? "From ₱100 up to ₱50,000 per transaction. Contact support to request a higher limit."
                        : depositType === "crypto"
                        ? "Minimum deposit of ₱200."
                        : depositType === "bank"
                        ? "From ₱200 up to ₱50,000 per transaction. Contact support to request a higher limit."
                        : "From ₱100 up to ₱300,000 per transaction. Contact support to request a higher limit."}
                    </div>
                  </div>
                  <br />
                  <div
                    className="deposit-promotion-divider"
                    style={{ paddingBlock: "10px" }}
                  ></div>

                  {/* ✅ Always show bonus list, even if something is selected */}
                  <div className="deposit-promotion-section">
                    <div className="deposit-promotion-content">
                      {bonusList.map((bonus) => {
                        const bonusName = bonus.bonusnameEN.includes(
                          "Default Non-Bonus Deposit"
                        )
                          ? "No Bonus"
                          : bonus.bonusnameEN

                        const minRequired =
                          newMemberBonusRequirements[bonus.bonusid] ||
                          ttacMinDeposit[bonus.bonusid] ||
                          0

                        const depositValue = parseCurrency(bankInput)

                        const isTTAC = Object.prototype.hasOwnProperty.call(
                          ttacMinDeposit,
                          bonus.bonusid
                        )

                        const isEligible = isTTAC
                          ? depositValue === minRequired //  exact match for ttacMinDeposit
                          : depositValue >= minRequired //  normal behavior for others

                        return (
                          <div
                            key={bonus.bonusid}
                            className={`deposit-promotion-item ${
                              selectedPromotion?.id === bonus.bonusid
                                ? "selected"
                                : ""
                            } ${
                              !isEligible && minRequired > 0 ? "disabled" : ""
                            }`}
                            onClick={() => {
                              if (!isEligible && minRequired > 0) {
                                showMessageWithTimeout(
                                  `Minimum deposit for this bonus is ₱${minRequired}.`,
                                  setAlertMessage
                                )
                                return
                              }

                              setSelectedPromotion({
                                id: bonus.bonusid,
                                promotion: bonus.bonusnameEN,
                                bonus: bonus.bonusnameEN,
                                label: bonus.bonusnameEN,
                                minDeposit: minRequired,
                              })
                            }}
                            style={
                              !isEligible && minRequired > 0
                                ? { opacity: 0.5, pointerEvents: "none" }
                                : {}
                            }
                          >
                            <div className="deposit-promotion-details">
                              <img
                                src="/images/icon/deposit-promotion-icon.webp"
                                alt={bonusName}
                              />
                              <div className="deposit-promotion-featured">
                                <label>{bonusName}</label>
                                {minRequired > 0 && (
                                  <>
                                    <small>
                                      {Object.prototype.hasOwnProperty.call(
                                        ttacMinDeposit,
                                        bonus.bonusid
                                      )
                                        ? `Exact Amount ₱${minRequired}`
                                        : `Min ₱${minRequired}`}
                                    </small>
                                    {isTTAC && (
                                      <>
                                        <br />
                                        <small
                                          style={{
                                            display: "block",
                                          }}
                                        >
                                          5X rollover req (slots only)
                                        </small>
                                      </>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                            <div className="deposit-promotion-divider"></div>
                            <div className="deposit-promotion-cta">
                              <div>
                                {selectedPromotion?.id === bonus.bonusid
                                  ? "Applied"
                                  : "Apply"}
                              </div>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>

                  <div className="transaction-field-message error">
                    {alertMessage}
                  </div>

                  <div className="deposit-submit-btn-container">
                    <button
                      type="submit"
                      className="deposit-submit-btn"
                      disabled={isSubmitDisabled || processing}
                      onClick={
                        depositType === "crypto"
                          ? handleSubmitCryptoBtn
                          : handleSubmitBtn
                      }
                      style={
                        isSubmitDisabled || processing
                          ? { background: "#9c9c9c", cursor: "not-allowed" }
                          : {}
                      }
                    >
                      {processing ? "Processing..." : "Continue Cash in"}
                    </button>
                  </div>
                </form>
                {depositType == "crypto" && (
                  <form
                    id="direpayForm2"
                    action={direpayForm2Data.action}
                    method="POST"
                    target="_top"
                    style={{ display: "none" }}
                    encType="multipart/form-data"
                  >
                    <input
                      type="hidden"
                      name="data"
                      value={direpayForm2Data.data}
                    />
                    <input
                      type="hidden"
                      name="platform"
                      value={direpayForm2Data.platform}
                    />
                    <input
                      type="hidden"
                      name="lang"
                      value={direpayForm2Data.lang}
                    />
                  </form>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
